import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';

// project import
import { handlerActiveItem, useGetMenuMaster } from '../../../../../api/menu';

export default function NavItem({ item, level }) {
  const theme = useTheme();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const openItem = menuMaster.openedItem;

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }
  let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item.url, end: false }, pathname) || openItem === item.id;

  // active menu item on page load
  useEffect(() => {
    if (pathname === item.url) handlerActiveItem(item.id);
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = theme.palette.text.primary;
  const iconSelectedColor = theme.palette.primary.main;

  const button = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => handlerActiveItem(item.id)}
      selected={isSelected}
      sx={{
        position: 'relative',
        zIndex: 1,
        borderRadius: 1.5,
        mb: 0.5,
        pl: drawerOpen ? `${Math.max(level * 18, 14)}px` : 1,
        pr: 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1.1,
        color: textColor,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.07)
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRight: 0,
            color: iconSelectedColor,
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: alpha(theme.palette.primary.main, 0.12)
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.07)
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, 0.1)
          }
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 34,
            color: isSelected ? iconSelectedColor : textColor,
            ...(!drawerOpen && {
              borderRadius: 1.5,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.07)
              }
            }),
            ...(!drawerOpen &&
              isSelected && {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.12)
              }
            })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: isSelected ? 700 : 600 }}>
              {item.title}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );

  return (
    item.visible !== false ? (
      drawerOpen ? button : (
        <Tooltip title={item.title} placement="right">
          {button}
        </Tooltip>
      )
    ) : null
  );
}

NavItem.propTypes = { item: PropTypes.object, level: PropTypes.number };
